/**
 * Serbian and Macedonian Cyrillic ↔ Latin.
 *
 * Sources:
 *
 * - Serbian — Gaj's Latin alphabet (Ljudevit Gaj, 1830s), which Vuk Karadžić's Cyrillic reform
 *   left in one-to-one correspondence with Serbian Cyrillic: 30 letters, with lj, nj and dž as
 *   digraphs for љ, њ and џ (Wikipedia, "Gaj's Latin alphabet", citing Babić et al. 2007 and
 *   Пипер et al. 2022). The values are identical in the UNGEGN 1977 system and in ALA-LC, as
 *   the T. T. Pedersen comparison table "Serbian" (rev. 2.0, 2007) shows. A Title-case digraph
 *   keeps its second letter lower case (Njemačka) unless the whole word is capitalized
 *   (NJEMAČKA). Where lj, nj or dž stand for two letters (nadživeti, injekcija, konjunkcija)
 *   the Latin cannot be told from the digraph without a dictionary; a short list of stems is
 *   built in and "+" between two letters forces them apart (the convention used by the
 *   Lexilogos converter).
 * - Macedonian — Gaj's alphabet with slight changes, as in the 1970 Pravopis na makedonskiot
 *   literaturen jazik (Vidoeski et al., p. 99) and Lunt (1952): ǵ and ḱ for ѓ and ќ, dz for ѕ,
 *   and lj / nj (also ĺ / ń) for љ and њ. The digraph system used for official documents since
 *   2008 (ICAO Doc 9303) writes gj kj zh ch sh dz dj lj nj. Both are described in the
 *   Wikipedia article "Romanization of Macedonian".
 *
 * What a program cannot do: choose between the digraph and the two-letter reading of lj, nj and
 * dž going back to Cyrillic, or between dz as ѕ and д + з in Macedonian. Russian, Ukrainian,
 * Belarusian and Bulgarian are handled by the Cyrillic Transliterator, not here.
 */

export type SerbianScheme = "sr" | "mk-academy" | "mk-digraph";

export interface SerbianOptions {
  /** Serbian: write and read đ as dj (a fallback still met in English texts). */
  djForDje: boolean;
}

export interface SerbianResult {
  text: string;
  /** Characters the scheme has no value for, left unchanged. */
  unknown: string[];
}

/** Lower-case Cyrillic letter, then its Latin value in Serbian, Macedonian (Academy) and Macedonian (digraph). */
const LETTERS: readonly (readonly [string, string, string, string])[] = [
  ["а", "a", "a", "a"],
  ["б", "b", "b", "b"],
  ["в", "v", "v", "v"],
  ["г", "g", "g", "g"],
  ["д", "d", "d", "d"],
  ["ђ", "đ", "", ""],
  ["ѓ", "", "ǵ", "gj"],
  ["е", "e", "e", "e"],
  ["ж", "ž", "ž", "zh"],
  ["з", "z", "z", "z"],
  ["ѕ", "", "dz", "dz"],
  ["и", "i", "i", "i"],
  ["ј", "j", "j", "j"],
  ["к", "k", "k", "k"],
  ["л", "l", "l", "l"],
  ["љ", "lj", "lj", "lj"],
  ["м", "m", "m", "m"],
  ["н", "n", "n", "n"],
  ["њ", "nj", "nj", "nj"],
  ["о", "o", "o", "o"],
  ["п", "p", "p", "p"],
  ["р", "r", "r", "r"],
  ["с", "s", "s", "s"],
  ["т", "t", "t", "t"],
  ["ћ", "ć", "", ""],
  ["ќ", "", "ḱ", "kj"],
  ["у", "u", "u", "u"],
  ["ф", "f", "f", "f"],
  ["х", "h", "h", "h"],
  ["ц", "c", "c", "c"],
  ["ч", "č", "č", "ch"],
  ["џ", "dž", "dž", "dj"],
  ["ш", "š", "š", "sh"],
];

const COLUMN: Record<SerbianScheme, 1 | 2 | 3> = {
  sr: 1,
  "mk-academy": 2,
  "mk-digraph": 3,
};

function schemeTable(scheme: SerbianScheme): Map<string, string> {
  const table = new Map<string, string>();
  for (const row of LETTERS) {
    const value = row[COLUMN[scheme]] as string;
    if (value) {
      table.set(row[0], value);
    }
  }
  return table;
}

const FORWARD: Record<SerbianScheme, Map<string, string>> = {
  sr: schemeTable("sr"),
  "mk-academy": schemeTable("mk-academy"),
  "mk-digraph": schemeTable("mk-digraph"),
};

/** Stems in which lj, nj or dž are two letters, not a digraph: [Latin, Cyrillic]. */
const SPLIT_STEMS: readonly (readonly [string, string])[] = [
  ["injekc", "инјекц"],
  ["injekt", "инјект"],
  ["konjunkc", "конјункц"],
  ["nadživ", "наджив"],
  ["dablju", "даблју"],
];

// Unicode's single-character digraphs (U+01C4–U+01CC) read back as one Cyrillic letter.
const PRECOMPOSED: Record<string, string> = {
  Ǆ: "Џ",
  ǅ: "Џ",
  ǆ: "џ",
  Ǉ: "Љ",
  ǈ: "Љ",
  ǉ: "љ",
  Ǌ: "Њ",
  ǋ: "Њ",
  ǌ: "њ",
};
const PRECOMPOSED_PATTERN = /[Ǆ-ǌ]/g;

const CYRILLIC_LOWER = /^[а-яёђѓѕјљњћќџ]$/;
const LETTER = /\p{L}/u;

function lowerOf(ch: string): string {
  return ch.toLowerCase();
}

function isUpper(ch: string | undefined): boolean {
  return (
    ch !== undefined && lowerOf(ch) !== ch && CYRILLIC_LOWER.test(lowerOf(ch))
  );
}

function capitalizeFirst(text: string): string {
  const first = [...text][0] ?? "";
  return first.toUpperCase() + text.slice(first.length);
}

// ---------------------------------------------------------------------------------------
// Cyrillic → Latin
// ---------------------------------------------------------------------------------------

/** Convert Serbian or Macedonian Cyrillic to Latin; other characters pass through. */
export function cyrillicToLatin(
  input: string,
  scheme: SerbianScheme,
  options: SerbianOptions
): SerbianResult {
  const table = FORWARD[scheme];
  const chars = [...input.normalize("NFC")];
  const unknown = new Set<string>();
  let out = "";
  for (let i = 0; i < chars.length; i += 1) {
    const ch = chars[i] as string;
    const lower = lowerOf(ch);
    let value = table.get(lower);
    if (value === undefined) {
      if (CYRILLIC_LOWER.test(lower)) {
        unknown.add(ch);
      }
      out += ch;
      continue;
    }
    if (scheme === "sr" && options.djForDje && lower === "ђ") {
      value = "dj";
    }
    if (ch === lower) {
      out += value;
    } else if (
      value.length > 1 &&
      (isUpper(chars[i - 1]) || isUpper(chars[i + 1]))
    ) {
      out += value.toUpperCase();
    } else {
      out += capitalizeFirst(value);
    }
  }
  return { text: out.normalize("NFC"), unknown: [...unknown] };
}

// ---------------------------------------------------------------------------------------
// Latin → Cyrillic
// ---------------------------------------------------------------------------------------

const MAX_KEY = 2;
const SEPARATOR = "+";

function reverseTable(
  scheme: SerbianScheme,
  options: SerbianOptions
): Map<string, string> {
  const table = new Map<string, string>();
  for (const [cyrillic, value] of FORWARD[scheme]) {
    table.set(value.normalize("NFC"), cyrillic);
  }
  if (scheme === "sr" && options.djForDje) {
    table.set("dj", "ђ");
  }
  if (scheme !== "sr") {
    table.set("ĺ", "љ");
    table.set("ń", "њ");
    table.set("ǵ", "ѓ");
    table.set("ḱ", "ќ");
  }
  return table;
}

function matchLatin(
  chars: readonly string[],
  at: number,
  table: ReadonlyMap<string, string>
): { cyrillic: string; length: number } | undefined {
  for (
    let length = Math.min(MAX_KEY, chars.length - at);
    length > 0;
    length -= 1
  ) {
    const key = chars
      .slice(at, at + length)
      .join("")
      .toLowerCase();
    const cyrillic = table.get(key);
    if (cyrillic) {
      return { cyrillic, length };
    }
  }
  return;
}

function matchStem(
  chars: readonly string[],
  at: number
): { cyrillic: string; length: number } | undefined {
  for (const [latin, cyrillic] of SPLIT_STEMS) {
    const slice = chars.slice(at, at + latin.length).join("");
    if (slice.toLowerCase() === latin) {
      const upperWord = slice.length > 1 && slice === slice.toUpperCase();
      const cased = upperWord ? cyrillic.toUpperCase() : cyrillic;
      const capital = slice[0] !== slice[0]?.toLowerCase() && !upperWord;
      return {
        cyrillic: capital ? capitalizeFirst(cased) : cased,
        length: latin.length,
      };
    }
  }
  return;
}

/** A "+" between two letters keeps them from forming a digraph. */
function isSeparator(chars: readonly string[], i: number): boolean {
  return (
    chars[i] === SEPARATOR &&
    LETTER.test(chars[i - 1] ?? " ") &&
    LETTER.test(chars[i + 1] ?? " ")
  );
}

/** Convert Latin text to Serbian or Macedonian Cyrillic. */
export function latinToCyrillic(
  input: string,
  scheme: SerbianScheme,
  options: SerbianOptions
): SerbianResult {
  const table = reverseTable(scheme, options);
  const chars = [
    ...input
      .normalize("NFC")
      .replace(PRECOMPOSED_PATTERN, (c) => PRECOMPOSED[c] ?? c),
  ];
  const unknown = new Set<string>();
  let out = "";
  let i = 0;
  while (i < chars.length) {
    const ch = chars[i] as string;
    const wordStart = !LETTER.test(chars[i - 1] ?? " ");
    if (CYRILLIC_LOWER.test(lowerOf(ch))) {
      out += ch;
      i += 1;
      continue;
    }
    if (isSeparator(chars, i)) {
      i += 1;
      continue;
    }
    const stem = scheme === "sr" && wordStart ? matchStem(chars, i) : undefined;
    if (stem) {
      out += stem.cyrillic;
      i += stem.length;
      continue;
    }
    const hit = matchLatin(chars, i, table);
    if (hit) {
      const slice = chars.slice(i, i + hit.length).join("");
      const upper = slice !== slice.toLowerCase();
      out += upper ? capitalizeFirst(hit.cyrillic) : hit.cyrillic;
      i += hit.length;
      continue;
    }
    if (LETTER.test(ch)) {
      unknown.add(ch);
    }
    out += ch;
    i += 1;
  }
  return { text: out.normalize("NFC"), unknown: [...unknown] };
}

// ---------------------------------------------------------------------------------------
// Letter table
// ---------------------------------------------------------------------------------------

export interface SerbianLetterRow {
  cyrillic: string;
  serbian: string;
  macedonianAcademy: string;
  macedonianDigraph: string;
}

/** The letter table: both cases of each Cyrillic letter and its value in each scheme. */
export function serbianLetterRows(): SerbianLetterRow[] {
  return LETTERS.map(([cyrillic, serbian, academy, digraph]) => ({
    cyrillic: `${cyrillic.toUpperCase()} ${cyrillic}`,
    serbian,
    macedonianAcademy: academy,
    macedonianDigraph: digraph,
  }));
}

export const SERBIAN_SCHEMES: readonly SerbianScheme[] = [
  "sr",
  "mk-academy",
  "mk-digraph",
];
